/**
 * EspotoQR Evaluator - JavaScript equivalent of Kotlin EspotoQR class
 * Handles QR code parsing, type evaluation, and task ID extraction
 */

// QR Type enumeration
export const EspotoQRType = {
    Invalid: 'Invalid',
    TaskQR: 'TaskQR',
    EventQR: 'EventQR',
    EventDemoUserQR: 'EventDemoUserQR',
    EventAccountQR: 'EventAccountQR'
};

/**
 * EspotoQR Evaluator Class
 * Parses and evaluates QR codes for the Espoto webapp
 */
export class EspotoQREvaluator {
    constructor(rawQR = '', spotMixinContext = null) {
        this.rawQR = rawQR;
        this.basicQR = '';
        this.espotoQRType = EspotoQRType.Invalid;
        this.eventId = '';
        this._spotMixinContext = spotMixinContext;
        
        this.parseRawQR();
    }

    /**
     * Parse the raw QR code string to extract basic QR data and event ID
     * @private
     */
    parseRawQR() {
        if (!this.rawQR || this.rawQR.trim() === '') {
            return;
        }

        try {
            const url = new URL(this.rawQR);
            const scheme = url.protocol;
            const segments = this.rawQR.split('/');

            // Check if it's an HTTP URL or matches target scheme
            if (scheme.startsWith('http') || this.isTargetScheme(scheme)) {
                for (let i = 0; i < segments.length - 1; i++) {
                    const segment = segments[i];
                    
                    if (segment === 'e') {
                        try {
                            this.eventId = segments[i + 1];
                        } catch (error) {
                            console.error('Could not convert String to eventId:', segments[i + 1]);
                        }
                    } else if (segment === 'd') {
                        this.basicQR = segments[i + 1];
                    }
                }

                // Webapp deep links carry e/d as query parameters instead of path segments
                // (https://<host>/?e=1061&d=<base64>). Base64 never contains spaces, so a '+'
                // that URLSearchParams decoded to a space is restored.
                if (!this.eventId) {
                    this.eventId = url.searchParams.get('e') || '';
                }
                if (!this.basicQR) {
                    this.basicQR = (url.searchParams.get('d') || '').replace(/ /g, '+');
                }
            }
        } catch (error) {
            console.info('Could not parse String to URL:', this.rawQR);
        }

        // If no basic QR was extracted, use the raw QR as basic QR
        if (!this.basicQR || this.basicQR.trim() === '') {
            this.basicQR = this.rawQR;
        }

        this.evalQRType();
    }

    /**
     * Check if the scheme matches the target scheme
     * @param {string} scheme - The URL scheme to check
     * @returns {boolean} - True if scheme matches target
     * @private
     */
    isTargetScheme(scheme) {
        // This would need to be configured based on your app's target scheme
        // For now, we'll check for common schemes
        const targetSchemes = ['espoto:', 'tabgame:', 'qr:'];
        return targetSchemes.some(targetScheme => scheme.startsWith(targetScheme));
    }

    /**
     * Evaluate the QR code type based on the basic QR content
     * @private
     */
    evalQRType() {
        let decoded = '';
        
        try {
            decoded = this.decodeBase64(this.basicQR);
        } catch (error) {
            console.info('Could not decode event QR:', this.basicQR);
            decoded = '';
        }

        // Check for basic task QR patterns
        if (this.basicQR.startsWith('qr_') || this.basicQR.startsWith('wp_')) {
            this.espotoQRType = EspotoQRType.TaskQR;
        } 
        // Check if it's a task QR with custom content
        else if (this.findTaskByQrWithPassword(this.basicQR) !== null) {
            this.espotoQRType = EspotoQRType.TaskQR;
        } 
        // Check decoded content for event-related QR types
        else if (decoded && decoded.trim() !== '') {
            if (decoded.endsWith('_autoLogin')) {
                // Example: 3105_69836a89f3268c1ac92caca3d34794eae441b0de_353_autoLogin
                this.espotoQRType = EspotoQRType.EventDemoUserQR;
            } else if (decoded.endsWith('_teamLogin')) {
                // Example: 3149_5bb7995460a383b6809d49619ec0709908a86f9a_353_teamLogin
                this.espotoQRType = EspotoQRType.EventDemoUserQR;
            } else if (decoded.includes('_autoLogin_') || decoded.includes('_teamLogin_')) {
                // Bound to an account, the last segment is its hash. A team QR bound to a team member
                // carries teamLogin instead of autoLogin. Examples:
                // 3105_69836a89f3268c1ac92caca3d34794eae441b0de_353_autoLogin_ff8aeaff026c340b723283444d72b1ddfd67285e
                // 3149_5bb7995460a383b6809d49619ec0709908a86f9a_353_teamLogin_ff8aeaff026c340b723283444d72b1ddfd67285e
                this.espotoQRType = EspotoQRType.EventAccountQR;
            } else if (/^[a-z\d_]+/.test(decoded)) {
                // Example: 317_8a24336f1fe301d440bc1c0e37925647583ad5e2_353
                this.espotoQRType = EspotoQRType.EventQR;
            } else {
                this.espotoQRType = EspotoQRType.Invalid;
            }

            // Links like /checklogin?d=<data> and PIN responses carry no event id, but every
            // event QR payload encodes it as its third segment
            if (!this.eventId && this.espotoQRType !== EspotoQRType.Invalid) {
                this.eventId = this.readEventIdFromDecodedData(decoded);
            }
        }
    }

    /**
     * Event id from a decoded event QR payload (<qr_id>_<hash>_<microevent_id>[_autoLogin...]),
     * empty string if the third segment is not numeric
     * @param {string} decoded - Decoded base64 payload
     * @returns {string}
     * @private
     */
    readEventIdFromDecodedData(decoded) {
        const segments = decoded.split('_');
        const eventId = segments.length > 2 ? segments[2].trim() : '';
        return /^\d+$/.test(eventId) ? eventId : '';
    }

    /**
     * Decode base64 string
     * @param {string} str - Base64 encoded string
     * @returns {string} - Decoded string
     * @private
     */
    decodeBase64(str) {
        try {
            return atob(str);
        } catch (error) {
            throw new Error('Invalid base64 string');
        }
    }

    /**
     * Find task by QR code with password using spot-mixin
     * @param {string} qrCode - QR code to search for
     * @returns {Object|null} - Task object or null if not found
     * @private
     */
    findTaskByQrWithPassword(qrCode) {
        // This method will be called from within a Vue component context
        // that has access to spot-mixin methods
        if (this._spotMixinContext && typeof this._spotMixinContext.getSpotByPassword === 'function') {
            return this._spotMixinContext.getSpotByPassword(qrCode);
        }
        return null;
    }

    /**
     * Read task ID from QR code
     * @returns {number} - Task ID or 0 if not found/invalid
     */
    readTaskIdFromQRCode() {
        if (this.espotoQRType !== EspotoQRType.TaskQR) {
            return 0;
        }

        const task = this.findTaskByQrWithPassword(this.basicQR);
        if (task) {
            return task.taskId || task.id || 0;
        }

        const content = this.basicQR.split('_').filter(part => part.trim() !== '');
        
        if (content.length !== 2) {
            return 0;
        }

        let wpId = 0;
        let type = '';
        
        try {
            type = content[0];
            wpId = parseInt(content[content.length - 1], 10);
        } catch (error) {
            return 0;
        }

        if (type.toLowerCase() !== 'wp' && type.toLowerCase() !== 'qr') {
            return 0;
        }

        return wpId;
    }

    /**
     * Get string representation of the QR evaluator
     * @returns {string} - String representation
     */
    toString() {
        let result = `EspotoQR [espotoQRType=${this.espotoQRType}, basicQR=${this.basicQR}`;
        
        if (this.eventId && this.eventId.trim() !== '') {
            result += `, eventId=${this.eventId}`;
        }
        
        if (this.espotoQRType === EspotoQRType.TaskQR) {
            result += `, taskId=${this.readTaskIdFromQRCode()}`;
        }
        
        result += ']';
        return result;
    }

    /**
     * Get basic QR code
     * @returns {string} - Basic QR code string
     */
    getBasicQR() {
        return this.basicQR;
    }

    /**
     * Get QR type
     * @returns {string} - QR type
     */
    getQRType() {
        return this.espotoQRType;
    }

    /**
     * Get event ID
     * @returns {string} - Event ID
     */
    getEventId() {
        return this.eventId;
    }

    /**
     * Check if QR is valid
     * @returns {boolean} - True if QR is valid
     */
    isValid() {
        return this.espotoQRType !== EspotoQRType.Invalid;
    }

    /**
     * Check if QR is a task QR
     * @returns {boolean} - True if QR is a task QR
     */
    isTaskQR() {
        return this.espotoQRType === EspotoQRType.TaskQR;
    }

    /**
     * Check if QR is an event QR
     * @returns {boolean} - True if QR is an event QR
     */
    isEventQR() {
        return this.espotoQRType === EspotoQRType.EventQR;
    }

    /**
     * Check if QR is a demo user QR
     * @returns {boolean} - True if QR is a demo user QR
     */
    isDemoUserQR() {
        return this.espotoQRType === EspotoQRType.EventDemoUserQR;
    }

    /**
     * Check if QR is an account QR
     * @returns {boolean} - True if QR is an account QR
     */
    isAccountQR() {
        return this.espotoQRType === EspotoQRType.EventAccountQR;
    }
}

/**
 * Factory function to create EspotoQR evaluator
 * @param {string} rawQR - Raw QR code string
 * @param {Object} spotMixinContext - Context object with spot-mixin methods
 * @returns {EspotoQREvaluator} - New EspotoQR evaluator instance
 */
export function createEspotoQREvaluator(rawQR = '', spotMixinContext = null) {
    return new EspotoQREvaluator(rawQR, spotMixinContext);
}

/**
 * Utility function to quickly evaluate a QR code
 * @param {string} rawQR - Raw QR code string
 * @param {Object} spotMixinContext - Context object with spot-mixin methods
 * @returns {Object} - Evaluation result with type, basicQR, eventId, and taskId
 */
export function evaluateQRCode(rawQR, spotMixinContext = null) {
    const evaluator = new EspotoQREvaluator(rawQR, spotMixinContext);
    
    return {
        type: evaluator.getQRType(),
        basicQR: evaluator.getBasicQR(),
        eventId: evaluator.getEventId(),
        taskId: evaluator.readTaskIdFromQRCode(),
        isValid: evaluator.isValid(),
        isTaskQR: evaluator.isTaskQR(),
        isEventQR: evaluator.isEventQR(),
        isDemoUserQR: evaluator.isDemoUserQR(),
        isAccountQR: evaluator.isAccountQR(),
        toString: () => evaluator.toString()
    };
}
